import { InjectionToken } from '@angular/core';
import { LocalStorageService } from '@cdw-ae/local-storage';
import { environment } from '../../../environments/environment';

/**
 * EDUS-2655 — timings for the automated session inactivity timeout.
 *
 * These are read once, when activity tracking starts, and copied into
 * `SessionState` so the reducer and selectors can derive warning/expiry
 * without reaching outside the store. See `app-lifecycles.models.ts`.
 */
export interface SessionTimeoutConfig {
  /** Idle time before the warning modal appears. FR-2: 29 minutes. */
  idleWarningAfterMs: number;
  /** Countdown length once the warning is showing. FR-2: 60 seconds. */
  warningDurationMs: number;
  /**
   * Minimum interval between registrations of user activity. Mouse movement
   * fires dozens of times a second; without this the store would be flooded
   * (and, from EDUS-2658, localStorage written) at that rate.
   *
   * Being up to this stale costs nothing: the value is measured against a
   * 29 minute threshold. Used by the activity service, not by the store.
   */
  activityThrottleMs: number;
}

export const DEFAULT_SESSION_TIMEOUT_CONFIG: SessionTimeoutConfig = {
  idleWarningAfterMs: 29 * 60_000,
  warningDurationMs: 60_000,
  activityThrottleMs: 1_000,
};

/**
 * Non-production override hook. The QA suite for this epic cannot wait 29
 * minutes per test case, so outside production the timings may be shortened
 * by writing partial JSON to this localStorage key before the app boots:
 *
 *   localStorage.setItem('gopher.session.testConfig', '{"idleWarningAfterMs":3000}')
 *
 * Ignored entirely when `environment.production` is true.
 */
export const SESSION_TIMEOUT_TEST_CONFIG_KEY = 'gopher.session.testConfig';

/**
 * Reads and validates the non-production override. Anything malformed, absent,
 * non-numeric or non-positive is discarded — a bad override must degrade to the
 * production defaults rather than disable the timeout.
 */
export function readSessionTimeoutTestOverride(): Partial<SessionTimeoutConfig> {
  if (environment.production) {
    return {};
  }

  let parsed: unknown;
  try {
    const raw = LocalStorageService.get(SESSION_TIMEOUT_TEST_CONFIG_KEY);
    if (!raw) {
      return {};
    }
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }

  if (typeof parsed !== 'object' || parsed === null) {
    return {};
  }

  const candidate = parsed as Record<string, unknown>;
  const override: Partial<SessionTimeoutConfig> = {};
  const keys: Array<keyof SessionTimeoutConfig> = [
    'idleWarningAfterMs',
    'warningDurationMs',
    'activityThrottleMs',
  ];

  for (const key of keys) {
    const value = candidate[key];
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      override[key] = value;
    }
  }

  return override;
}

export function sessionTimeoutConfigFactory(): SessionTimeoutConfig {
  return {
    ...DEFAULT_SESSION_TIMEOUT_CONFIG,
    ...readSessionTimeoutTestOverride(),
  };
}

/**
 * Tree-shakeable root token — deliberately NOT added to the `app.config.ts`
 * providers array. That array has a documented ordering sensitivity with NgRx
 * in bundled builds (see docs/frontend/ngrx-provider-patterns.md, EDUS-1770);
 * a `providedIn: 'root'` factory sidesteps it entirely.
 */
export const SESSION_TIMEOUT_CONFIG = new InjectionToken<SessionTimeoutConfig>(
  'SESSION_TIMEOUT_CONFIG',
  {
    providedIn: 'root',
    factory: sessionTimeoutConfigFactory,
  }
);
